import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  anchoredPan,
  clampPan,
  fitPan,
  fitZoom,
  stepZoom,
  wheelZoom,
  type BoardPan,
} from '../../../lib/team-meeting-board.js';

/** ドラッグで動かすのを始めない要素（押して操作するもの）。 @graph-connects none */
const INTERACTIVE = 'button, a, input, textarea, select, label, [role="dialog"]';

/** 操作ボタン 1 つ。 @graph-connects none */
function ToolButton({
  label,
  onClick,
  children,
  isPressed,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  isPressed?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={isPressed}
      title={label}
      onClick={onClick}
      className="flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-sm text-[#1e1e1e] hover:bg-black/5 focus-visible:outline-2 focus-visible:outline-[#5b7fb5]"
    >
      {children}
    </button>
  );
}

/**
 * ボードを入れる表示枠（FigJam のキャンバスのように拡大・縮小・移動できる）。
 *
 * - 右下のボタンで 1 段ずつ拡大・縮小、等倍、横幅に合わせる、全体を収める、全画面
 * - ⌘ / Ctrl + ホイール（トラックパッドのピンチ）で指している所を中心に拡大・縮小
 * - 背景のドラッグ（付箋やボタンの上では動かさない）とホイール（2 本指）で動かす。スクロールではなく
 *   ボードの位置を持つので、左・上へもはみ出して動かせる（ボードが表示枠に少し残る所で止める）
 * - 表示枠の高さは右下の角を引いて変えられる
 *
 * 開いたときは横幅に収まる大きさにする（狭い画面でもボード全体の配置が見えるように）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のボードを FigJam のキャンバスのように拡大・縮小・ドラッグで移動でき、全画面にも広げられる表示枠に入れ、付箋が多い回でも全体を見渡せるようにする
 * @graph-connects fitZoom [calls] 表示枠に収める拡大率
 * @graph-connects stepZoom [calls] ボタンでの拡大・縮小
 * @graph-connects wheelZoom [calls] ホイールでの拡大・縮小
 * @graph-connects anchoredPan [calls] 指している点を残すボードの位置
 * @graph-connects clampPan [calls] ボードを見失わない範囲に収める
 * @graph-connects fitPan [calls] 収めたときのボードの位置
 */
export function BoardViewport({ children }: { children: ReactNode }): ReactNode {
  const { t } = useTranslation();
  const viewportRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const zoomRef = useRef(zoom);
  const boardRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState<BoardPan>({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number } | null>(null);

  // 表示枠の大きさと、いまの拡大率でのボードの大きさ
  const measure = () => {
    const el = viewportRef.current;
    const board = boardRef.current?.getBoundingClientRect();
    return {
      viewport: { width: el?.clientWidth ?? 0, height: el?.clientHeight ?? 0 },
      content: { width: board?.width ?? 0, height: board?.height ?? 0 },
    };
  };

  // ボードを動かす（ボードを見失う所までは行かせない）
  const moveBy = (dx: number, dy: number) => {
    const { viewport, content } = measure();
    setPan((p) => clampPan({ x: p.x + dx, y: p.y + dy }, viewport, content));
  };

  // 拡大率を変える（指している点、無ければ表示枠の中央を同じ場所に残す）
  const changeZoom = (next: number, at?: { x: number; y: number }) => {
    const from = zoomRef.current;
    if (next === from) return;
    const { viewport, content } = measure();
    const point = at ?? { x: viewport.width / 2, y: viewport.height / 2 };
    const ratio = { from, to: next };
    const scaled = { width: (content.width * next) / from, height: (content.height * next) / from };
    zoomRef.current = next;
    setZoom(next);
    setPan((p) =>
      clampPan(
        { x: anchoredPan(p.x, point.x, ratio), y: anchoredPan(p.y, point.y, ratio) },
        viewport,
        scaled,
      ),
    );
  };

  // 中身の大きさ（等倍）に対して表示枠に収め、収めたボードを中央に置く
  const fit = (mode: 'width' | 'all') => {
    const { viewport, content } = measure();
    const current = zoomRef.current;
    const natural = { width: content.width / current, height: content.height / current };
    const next = fitZoom(viewport, natural, mode);
    zoomRef.current = next;
    setZoom(next);
    setPan(fitPan(viewport, { width: natural.width * next, height: natural.height * next }, mode));
  };

  // 開いたときだけ横幅に収める（以降は人が決めた大きさを保つ）
  useEffect(() => {
    fit('width');
  }, []);

  // 全画面は Escape でも閉じる（開いた付箋のダイアログがあれば、そちらを先に閉じる）
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[role="dialog"]') !== null) return;
      setIsFullscreen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [isFullscreen]);

  // ⌘ / Ctrl + ホイールはブラウザの拡大を止めてボードだけを拡大・縮小し、ただのホイールはボードを
  // 動かす（どちらも passive では止められない）。Shift + 縦ホイールは横に動かす
  useEffect(() => {
    const el = viewportRef.current;
    if (el === null) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (!e.ctrlKey && !e.metaKey) {
        const isSideways = e.shiftKey && e.deltaX === 0;
        moveBy(-(isSideways ? e.deltaY : e.deltaX), isSideways ? 0 : -e.deltaY);
        return;
      }
      const rect = el.getBoundingClientRect();
      changeZoom(wheelZoom(zoomRef.current, e.deltaY), {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, []);

  return (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-[150] flex flex-col bg-[#f3f2ee] p-3'
          : 'relative overflow-hidden rounded-lg border border-[#e4e2dc] bg-[#f3f2ee]'
      }
    >
      <div
        ref={viewportRef}
        data-testid="board-viewport"
        className={`relative cursor-grab touch-none overflow-hidden active:cursor-grabbing ${
          isFullscreen ? 'flex-1 rounded-lg' : 'h-[75vh] min-h-[420px] resize-y'
        }`}
        onMouseDown={(e) => {
          // 動かしている間に付箋の文字が選択されないようにする（pointerdown では選択を止められない）
          if ((e.target as Element).closest(INTERACTIVE) === null) e.preventDefault();
        }}
        onPointerDown={(e) => {
          if (e.button !== 0 || (e.target as Element).closest(INTERACTIVE) !== null) return;
          dragRef.current = { x: e.clientX, y: e.clientY };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const drag = dragRef.current;
          if (drag === null) return;
          moveBy(e.clientX - drag.x, e.clientY - drag.y);
          dragRef.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUp={() => {
          dragRef.current = null;
        }}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
      >
        {/* 位置と拡大率は別の要素に掛ける（zoom を掛けた要素の left / top は拡大率ぶん伸びる）。transform は
            使わない（中の position: fixed の基準が変わる） */}
        <div
          data-testid="board-pan"
          className="absolute w-full lg:w-max"
          style={{ left: pan.x, top: pan.y }}
        >
          <div ref={boardRef} className="w-full p-3 lg:w-max" style={{ zoom }}>
            {children}
          </div>
        </div>
      </div>

      <div
        role="toolbar"
        aria-label={t('teamMeetings.board.zoom.toolbar')}
        className={`absolute z-20 flex items-center gap-0.5 rounded-lg border border-[#e4e2dc] bg-white/95 p-1 shadow-md ${
          isFullscreen ? 'bottom-6 right-6' : 'bottom-3 right-5'
        }`}
      >
        <ToolButton
          label={t('teamMeetings.board.zoom.out')}
          onClick={() => {
            changeZoom(stepZoom(zoomRef.current, 'out'));
          }}
        >
          −
        </ToolButton>
        <ToolButton
          label={t('teamMeetings.board.zoom.reset')}
          onClick={() => {
            changeZoom(1);
          }}
        >
          <span className="w-11 tabular-nums">{Math.round(zoom * 100)}%</span>
        </ToolButton>
        <ToolButton
          label={t('teamMeetings.board.zoom.in')}
          onClick={() => {
            changeZoom(stepZoom(zoomRef.current, 'in'));
          }}
        >
          ＋
        </ToolButton>
        <span className="mx-1 h-5 w-px bg-[#e4e2dc]" aria-hidden="true" />
        <ToolButton
          label={t('teamMeetings.board.zoom.fitWidth')}
          onClick={() => {
            fit('width');
          }}
        >
          ↔
        </ToolButton>
        <ToolButton
          label={t('teamMeetings.board.zoom.fitAll')}
          onClick={() => {
            fit('all');
          }}
        >
          ⤢
        </ToolButton>
        <ToolButton
          label={t(
            isFullscreen
              ? 'teamMeetings.board.zoom.exitFullscreen'
              : 'teamMeetings.board.zoom.fullscreen',
          )}
          isPressed={isFullscreen}
          onClick={() => {
            setIsFullscreen((v) => !v);
          }}
        >
          ⛶
        </ToolButton>
      </div>
    </div>
  );
}
